/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-shadow */
import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Badge,
  Button,
  message,
  Typography,
} from 'antd';
import moment from 'moment';
import TMIcon from 'tm-icons-library';

import { SyncOutlined } from '@ant-design/icons';

import {
  ServiceRequestSocketAPI,
} from '../../../../api/ServiceRequestSocketAPI';
import ChatInputBox from '../../../../components/ChatInputBox/ChatInputBox';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  uploadFilesForCommunity,
} from '../../../../context/thunks/communityThunk';
import {
  addOrEditMessage,
  fetchMoreMessages,
  markMessagesAsRead,
} from '../../../../context/thunks/tqConnectServiceRequestThunk';
import {
  clearServiceRequestChatRoom,
  setShowScrollToBottom,
  updateServiceRequestChatRoom,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  dynamicItemTitle,
  getReadableDateFormat,
} from '../../../../shared/utils';
import {
  SERVICE_REQUEST_SOCKET_ACTION_EVENTS,
} from '../../../../types/socketTypes';
import {
  IServiceRequestMessage,
  IServiceRequestMessageRoom,
  TQServiceRequestStatusType,
} from '../../../../types/tqConnectTypes/service-requests';
import CommunityChatbackgroundLayer
  from '../../../Community/components/CommunityChatbackgroundLayer';
import BidirectionalScroll, {
  BidirectionalScrollRef,
} from '../../../Messages/components/BidirectionalScroll';
import HeaderTextWithLine from '../HeaderTextWithLine/HeaderTextWithLine';
import MessageCard from './MessageCard/MessageCard';
import MessageCardSkeleton from './MessageCard/MessageCardSkeleton';

interface Props {
  serviceRequestId: string;
  roomId: string;
  status: TQServiceRequestStatusType;
  serviceRequestMessageRoomDetails: IServiceRequestMessageRoom;
  isFooterActionsVisible?: boolean;
}

const MessageRoomChats: React.FC<Props> = ({
  serviceRequestId,
  roomId,
  status,
  serviceRequestMessageRoomDetails,
  isFooterActionsVisible,
}) => {
  const { colors: COLORS } = useTheme();

  const dispatch = useAppDispatch();

  const {
    isServiceRequestSocketConnected,
    serviceRequestChatRoom,
    showScrollToBottom,
  } = useAppSelector((state) => state.tqConnectServiceRequest);

  const [isSending, setIsSending] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const scrollRef = useRef<BidirectionalScrollRef>(null);
  const loading = useRef<boolean>(false);

  // store the initial last seen and last message ids to compare with the new ones
  // this is used to check if new messages are received in the chat room
  const { prevLastSeenMessageId, prevLastMessageId } = useMemo(
    () => ({
      prevLastSeenMessageId: serviceRequestMessageRoomDetails?.lastSeen,
      prevLastMessageId: serviceRequestMessageRoomDetails?.lastMessage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const messagesList = useMemo(() => {
    const listItems: {
      type: 'message' | 'date' | 'header';
      message?: IServiceRequestMessage;
      date?: string;
      header?: string;
      sentByPrevSender?: boolean;
    }[] = [];
    let lastDate: string | null = null;

    serviceRequestChatRoom.messages.forEach((item, index) => {
      const messageDate = moment(item.createdAt).format('YYYY-MM-DD');
      const prevMessage = serviceRequestChatRoom.messages?.[index - 1];
      const prevSenderId = prevMessage?.sender?._id;
      const currentSenderId = item.sender?._id;

      // check if it is the last one in the group of messages sent by a user in a same date
      let isLastInGroup = currentSenderId !== prevSenderId;

      if (lastDate !== messageDate) {
        listItems.push({ type: 'date', date: messageDate });
        lastDate = messageDate;
        // show it as the last one in the group of messages sent by a user only when it's sent in the same date
        isLastInGroup = true;
      }

      listItems.push({
        type: 'message',
        message: item,
        sentByPrevSender: !isLastInGroup,
      });

      // check if the message is the last seen message but not the last message in the room and there are new messages below it
      if (
        item._id === prevLastSeenMessageId &&
        item._id !== prevLastMessageId
      ) {
        // count new messages below the last seen message:
        const lastSeenIndex = serviceRequestChatRoom.messages.findIndex(
          (m) => m._id === prevLastSeenMessageId,
        );
        const count = serviceRequestChatRoom.messages.length - lastSeenIndex;

        if (count > 0) {
          listItems.push({
            type: 'header',
            header: 'New messages',
          });
        }
      }
    });

    return listItems;
  }, [
    serviceRequestChatRoom.messages,
    prevLastMessageId,
    prevLastSeenMessageId,
  ]);

  const sendMessage = async (
    text: string,
    files?: File[],
    onSuccess?: () => void,
  ) => {
    let assets: string[] = [];

    if (files && files.length > 0) {
      const resp = await dispatch(
        uploadFilesForCommunity(files, setIsUploading),
      );

      if (resp && resp?.length > 0) {
        assets = resp.map((file) => file._id);
      }
    }

    setIsSending(true);

    ServiceRequestSocketAPI.emit(
      SERVICE_REQUEST_SOCKET_ACTION_EVENTS.ROOM_MESSAGE_CREATE,
      {
        room: roomId,
        message: text,
        assets,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (resp: any) => {
        if (resp?.status === 'success') {
          dispatch(addOrEditMessage(resp.result));
          onSuccess?.();
          setTimeout(() => {
            scrollRef.current?.scrollToBottom();
          }, 500);
        } else {
          message.error('Failed to send message');
        }
      },
    );

    setIsSending(false);
  };

  const loadMessages = async () => {
    if (
      serviceRequestId &&
      roomId &&
      roomId === serviceRequestChatRoom.roomId &&
      !loading.current
    ) {
      loading.current = true;

      await dispatch(
        fetchMoreMessages(serviceRequestId, roomId, 'up', () => {
          loading.current = false;
          scrollRef.current?.scrollToBottom();
        }),
      );

      // mark messages as read in the current room
      dispatch(
        markMessagesAsRead(
          roomId,
          serviceRequestMessageRoomDetails?.lastMessage,
        ),
      );
    }
  };

  // Bidrectional scroll event handlers
  const onStartReached = async () => {
    if (
      serviceRequestChatRoom.hasMore.previous &&
      !loading.current &&
      roomId &&
      serviceRequestId
    ) {
      loading.current = true;
      await dispatch(
        fetchMoreMessages(serviceRequestId, roomId, 'up', () => {
          loading.current = false;
        }),
      );
    }
  };

  const onEndReached = async () => {
    if (
      serviceRequestChatRoom.hasMore.next &&
      !loading.current &&
      roomId &&
      serviceRequestId
    ) {
      loading.current = true;
      await dispatch(
        fetchMoreMessages(serviceRequestId, roomId, 'down', () => {
          loading.current = false;
        }),
      );
    } else {
      dispatch(updateServiceRequestChatRoom({ newMessagesCount: 0 }));
    }
  };

  const scrollToBottom = async () => {
    dispatch(updateServiceRequestChatRoom({ newMessagesCount: 0 }));

    if (serviceRequestId && roomId) {
      if (serviceRequestChatRoom.hasMore.next) {
        // console.log('clearing chat room to scroll to bottom');
        dispatch(
          updateServiceRequestChatRoom({
            messages: [],
            roomId,
            allMessagesLoaded: false,
            unreadCount: 0,
            hasMore: {
              previous: true,
              next: false,
            },
          }),
        );
        await dispatch(
          fetchMoreMessages(serviceRequestId, roomId, 'up', () => {
            loading.current = false;
            scrollRef.current?.scrollToBottom();
          }),
        );
      } else {
        scrollRef.current?.scrollToBottom();
      }
    }
  };

  // Load messages on opening the chat room for the first time
  useEffect(() => {
    loadMessages();

    return () => {
      dispatch(clearServiceRequestChatRoom());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="message-room__chats">
        <BidirectionalScroll
          ref={scrollRef}
          className="message-room__chats__inner__scroll"
          onShowScrollButton={(show) => {
            if (showScrollToBottom !== show)
              dispatch(setShowScrollToBottom(show));
          }}
          onStartReached={onStartReached}
          onEndReached={onEndReached}>
          <div
            role="none"
            className="message-room__chats__inner__scroll__spacer"
          />

          {serviceRequestChatRoom.hasMore.previous &&
          serviceRequestChatRoom.loading ? (
            <>
              {Array(2)
                .fill(0)
                .map((_, index) => (
                  <MessageCardSkeleton key={index} />
                ))}
            </>
          ) : null}

          {messagesList.map((item, index) => {
            const { date, message, sentByPrevSender, header } = item;
            return (
              <React.Fragment
                key={
                  date
                    ? `date-${date}`
                    : message?._id
                      ? `message-${message._id}`
                      : header
                        ? `header-${header}`
                        : `item-${index}`
                }>
                {date ? (
                  <div
                    className="message-room__chats__body__date"
                    data-date={date}>
                    {getReadableDateFormat(date)}
                  </div>
                ) : null}

                {message ? (
                  <MessageCard
                    serviceRequestId={serviceRequestId}
                    message={message}
                    maxVisibleFiles={1}
                    hideHeader={sentByPrevSender}
                  />
                ) : null}

                {header ? (
                  <HeaderTextWithLine
                    alignment="center"
                    title={header}
                    titleStyle={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: COLORS.INFO_ALT,
                    }}
                    lineStyle={{ backgroundColor: COLORS.INFO200 }}
                    style={{ marginBlock: 16 }}
                  />
                ) : null}
              </React.Fragment>
            );
          })}

          {serviceRequestChatRoom.hasMore.next &&
          serviceRequestChatRoom.loading ? (
            <>
              {Array(2)
                .fill(0)
                .map((_, index) => (
                  <MessageCardSkeleton key={index} />
                ))}
            </>
          ) : null}

          {!isServiceRequestSocketConnected ? (
            <Typography.Paragraph
              type="secondary"
              className="message-room__chats__inner__scroll__alert">
              <SyncOutlined
                spin
                style={{
                  fontSize: 16,
                  color: COLORS.ICON,
                }}
              />
              Trying to connect...
            </Typography.Paragraph>
          ) : null}
        </BidirectionalScroll>

        {showScrollToBottom && serviceRequestChatRoom.messages.length > 0 ? (
          <Button className="scroll-down__btn" onClick={scrollToBottom}>
            <TMIcon name="chevron-down" color={COLORS.ICON} size={20} />
            <Badge
              size="small"
              count={serviceRequestChatRoom?.newMessagesCount}
              overflowCount={99}
              offset={[0, 0]}
              className="scroll-down__btn__count"
              style={{
                backgroundColor: COLORS.INFO_ALT,
              }}
            />
          </Button>
        ) : null}

        <CommunityChatbackgroundLayer styles={{ height: '100%' }} />
      </div>
      {
        // show messages footer with chat input box or disabled messaging state
        // only when the chat server is connected
        isServiceRequestSocketConnected ? (
          <div className="message-room__chats__footer">
            {status === 'completed' ? (
              <Typography.Paragraph
                type="secondary"
                style={{
                  margin: 0,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'center',
                  gap: 2,
                }}>
                <TMIcon
                  name="information-circle-outline"
                  color={COLORS.ICON}
                  size={20}
                  style={{
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                />{' '}
                {dynamicItemTitle('tq-offering-request', 'capital')} marked as
                complete — this chat is now read-only.
              </Typography.Paragraph>
            ) : (
              <ChatInputBox
                accept={['doc', 'image', 'video', 'audio']}
                isSending={isUploading || isSending}
                onSend={sendMessage}
                placeholder="Type a message..."
                isFooterActionsVisible={isFooterActionsVisible}
              />
            )}
          </div>
        ) : null
      }
    </>
  );
};

export default memo(MessageRoomChats);
